import { Component, OnInit } from '@angular/core';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { CalendarComponent } from '@common/UI/calendar/calendar.component';
import { NumberInputComponent } from '@common/UI/number-input/number-input.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { Lookup } from '@common/utilities/Lookup';
import { commit, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { DataService, GoodRecord, InvoiceCategory, InvoiceDetailRecord, InvoiceRecord, InvoiceState, invoiceState, InvoiceType, PartnerRecord, UserRecord, SKU2Parts, parts2SKU, ProductRecord, ProductUnit } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { ProductsService } from '@core/services/products.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { TagsService } from '@core/services/tags.service';
import { UsersService } from '@core/services/users.service';
import { Multilingual } from '@core/utilities/multilingual';
import { MessageBoxService } from '@routes/common/message-box.service';
import { ViewerComponent } from '@routes/invoices/viewer/viewer.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TextEditorComponent } from '@routes/editors/text-editor/text-editor.component';
import { ConfigsService } from '@core/services/configs.service';
import { ConfirmerComponent } from './confirmer/confirmer.component';


function getKey(info: Key): string {
  return [info.SKU, info.Color, info.Unit, info.ImportInvoiceTag, info.ExportUnitPrice, info.ActualQuantity, info.QuoteQuantity].join('/');
}


const lu = {
  goods: new Lookup<GoodRecord>(i => i.ID),
  returnable: new Lookup<ReturnableGroup>(i => getKey(i))
}

interface Key {
  SKU: string;
  Color: string;
  Unit: ProductUnit;
  ImportInvoiceTag: string;
  ExportUnitPrice: number;
  ActualQuantity: number;
  QuoteQuantity: number;
}

export interface ReturningGroup {
  Identified: boolean;
  SKU: string;
  Color: string;
  Unit: ProductUnit;
  ImportInvoiceTag: string;
  ExportUnitPrice: number;
  records: InvoiceDetailRecord[];
}

interface ReturnableGroup extends Key {
  records: GoodRecord[];
}

interface InvoiceGroup {
  partner: PartnerRecord;
  invoices: InvoiceRecord[];

  ConfirmedCount: number;
  ConfirmedStamp: number;

  EditableCount: number;
  EditableStamp: number;

  LengthCount: number;
  WeightCount: number;
  LengthActualQuantity: number;
  WeightActualQuantity: number;
  Total: number;
}

@Component({
  templateUrl: './returns.component.html',
  styleUrls: ['./returns.component.scss']
})
export class ReturnsComponent implements OnInit {

  constructor(
    private layout: LayoutService,
    private ds: DataService,
    private modal: ModalService,
    private api: ApiService,
    private sync: SynchronizationService,
    private mb: MessageBoxService,
    private _partners: PartnersService,
    private _users: UsersService,
    private _tags: TagsService,
    private _products: ProductsService,
    public configs: ConfigsService
  ) { }

  public get loading(): boolean {
    return !this.sync.ready(UsersService, PartnersService, TagsService, ReturnsComponent, ProductsService)
  }

  public get infinity(): number {
    return Infinity;
  }

  public importXlsx(text: string = '[MÃ HÀNG] [MÀU] [SỐ LƯỢNG] [CÂY 1] [CÂY 2] [CÂY 3] [CÂY 4] [CÂY 5] [CÂY 6] [MÉT TÍNH TIỀN] [GIÁ NHẬP]') {
    this.modal.show(TextEditorComponent, { state: { title: 'Nhập hàng khách trả bằng dữ liệu excel', text: text } }).content.commit.subscribe(text => {

      let lines = text.split('\n');

      let sku = '';
      let color = '';

      let records: { returnable: ReturnableGroup, sku: string, color: string, actual: number, quote: number, price: number }[] = [];
      let adjustments: { [key: string]: { sku: string, colors: string[], profit: number, logistics: number } } = {};
      let news: boolean = false;

      lines.forEach(line => {

        let cols = line.split('\t');

        if (cols.length < 4) return;

        cols[0] = cols[0].replace(/(#)/i, '').trim().toUpperCase();
        if (cols[0] != '') {
          let parts = SKU2Parts(cols[0]);
          sku = parts2SKU(parts[0], parts[1]);
        }

        cols[1] = cols[1].split(/(-|~|号|\()/i)[0].replace(/(#)/i, '').trim().toUpperCase();

        if (cols[1] != '') color = cols[1];

        for (let i = 3; i < Math.min(cols.length, 9); i++) {

          let length = parseFloat(cols[i].replace(',', ''));
          if (isNaN(length)) continue;

          let returnable = this.returnable.source.first(i => i.SKU == sku && i.Color == color && i.ActualQuantity == length);

          if (!returnable) returnable = this.returnable.source.first(i => i.SKU == sku && i.Color == color && i.ActualQuantity == Infinity);

          if (!returnable) {

            if (!adjustments[sku]) {
              adjustments[sku] = { sku: sku, colors: [], profit: this.configs.defaultProfitMargin, logistics: this.configs.defaultLogisticsMargin };
            }

            if (!adjustments[sku].colors.contains(color)) {
              adjustments[sku].colors.push(color);
            }

            news = true;
          }

          records.push({ returnable: returnable, sku: sku, color: color, actual: length, price: NaN, quote: NaN });
        }

        if (cols.length > 9) {
          let quote = parseFloat(cols[9].replace(',', ''));

          if (!isNaN(quote)) {

            let candidates = records.filter(i => isNaN(i.quote));

            if (candidates.length > 0) {
              let avg = Math.round((candidates.sum(i => i.actual) - quote) * 10 / candidates.length) / 10;              

              candidates.forEach(i => i.quote = i.actual - avg);
            }
          }
        }

        if (cols.length > 10) {
          let price = parseFloat(cols[10].replace(',', ''));
          if (!isNaN(price)) {
            records.forEach(record => {
              if (isNaN(record.price)) record.price = price;
            });
          }          
        }        
      });

      if (news) {

        let products: { sku: string, colors: string[], profit: number, logistics: number }[] = [];
        for (let o in adjustments) products.push(adjustments[o]);

        let confirmer = this.modal.show(ConfirmerComponent, { state: { products: products }, ignoreBackdropClick: true });
        confirmer.closable = false;

        confirmer.content.onCancel.subscribe(() => {
          this.importXlsx(text);
        });

        confirmer.content.onConfirm.subscribe(() => {

          for (let o in adjustments) {
            let product: ProductRecord = { ID: 0, SKU: adjustments[o].sku, Colors: adjustments[o].colors.map(i => ({ Available: 0, Color: i })), Unit: ProductUnit.Meter, AverageQuantity: 0, IsPriceVerified: false, UnitPrice: 0, ProfitMargin: adjustments[o].profit, LogisticsMargin: adjustments[o].logistics, Stamp: new Date().getTime() };
            this.sync.with(ReturnsComponent).repeat(() => this.ds.products('create', product).pipe(map(results => results && results.length == 1)));

            this.sync.when(ReturnsComponent).commit(() => {

              if (news) {
                this._products.synchronize();
              }

              this.sync.when(ProductsService).commit(() => {

                records.forEach(record => {

                  if (!record.returnable) record.returnable = this.returnable.source.first(i => i.SKU == sku && i.Color == color && i.ActualQuantity == Infinity);

                  if (!record.returnable) {
                    record.returnable = {
                      SKU: record.sku,
                      Color: record.color,
                      Unit: ProductUnit.Meter,
                      ActualQuantity: Infinity,
                      QuoteQuantity: Infinity,
                      ExportUnitPrice: 0,
                      ImportInvoiceTag: 'n/a',
                      records: []
                    };

                    lu.returnable.add(record.returnable);
                    this.returnable.source = lu.returnable.source;
                  }

                  this.add(record.returnable, record.actual, record.quote, record.price);

                });
              });
            });
          }
        });
      }
      else {

        this.sync.when(ReturnsComponent).commit(() => {

          records.forEach(record => {

            lu.returnable.add(record.returnable);
            this.returnable.source = lu.returnable.source;

            this.add(record.returnable, record.actual, record.quote, record.price);

          });

        });
      }
    });
  }

  public invoices = new Filterable<InvoiceRecord>({ extractor: i => [i.Tag, this._partners.get(i.PartnerID).Name], grouper: i => [{ name: "Khách hàng", option: this._partners.get(i.PartnerID).Name, description: 'K' + i.PartnerID }] });

  public returnable = new Filterable<ReturnableGroup>({
    extractor: i => [i.SKU + 'm' + i.Color + 'd' + i.ActualQuantity, i.SKU + 'd' + i.ActualQuantity],
    grouper: i => [{ name: 'Nguồn nhập', option: i.ImportInvoiceTag == 'n/a' ? Multilingual.getText('Hàng đã quyết toán').toUpperCase() : i.ImportInvoiceTag, description: i.ImportInvoiceTag == 'n/a' ? this.selected.partner.Name.toUpperCase() : this.source(i.ImportInvoiceTag).toUpperCase() }]
  });

  public partners = new Filterable<InvoiceGroup>({
    extractor: i => [i.partner.Name, 'K' + i.partner.ID],
    grouper: i => [
      { name: 'Nhóm khách hàng', option: i.partner.Address },
      { name: 'Đơn xong', option: i.ConfirmedCount > 0 ? 'Có đơn xong' : 'Không có đơn xong' },
      { name: 'Đơn chờ', option: i.EditableCount > 0 ? 'Có đơn chờ' : 'Không có đơn chờ' }
    ]
  });

  public all: InvoiceGroup = {
    partner: { ID: 0, Name: Multilingual.getText('Tất cả đơn hàng'), Address: '', Phone: '', Email: '', DealerID: 0, ExportPolicyID: 0, ImportPolicyID: 0 },
    invoices: [],
    ConfirmedCount: 0,
    ConfirmedStamp: 0,
    EditableCount: 0,
    EditableStamp: 0,
    LengthCount: 0,
    WeightCount: 0,
    LengthActualQuantity: 0,
    WeightActualQuantity: 0,
    Total: 0
  };

  public selected: { invoice: InvoiceRecord, details: ReturningGroup[], partner: PartnerRecord } = { invoice: null, partner: null, details: [] };
  private edited: boolean = false;

  private synchronize() {
    
    this.selected.invoice = null;
    this.selected.partner = null;
    this.invoices.source = [];

    this.all.invoices = [];

    this.sync.with(ReturnsComponent).repeat(() => this.ds.invoices('imports').pipe(map(data => data ? commit<boolean>(() => this.all.invoices = data.filter(i => i.PartnerID > 3)).return(true) : false)));

    this.sync.when(ReturnsComponent, UsersService, PartnersService).commit(() => {

      this.all = {
        ...this.all,
        ConfirmedCount: this.all.invoices.count(i => i.State == InvoiceState.Confirmed),
        EditableCount: this.all.invoices.count(i => i.State == InvoiceState.Editable),
        LengthCount: this.all.invoices.sum(i => i.LengthCount),
        WeightCount: this.all.invoices.sum(i => i.WeightCount),
        LengthActualQuantity: this.all.invoices.sum(i => i.LengthActualQuantity),
        WeightActualQuantity: this.all.invoices.sum(i => i.WeightActualQuantity),
        Total: this.all.invoices.sum(i => i.Total)
      }

      this.partners.source = this._partners.all.filter(i => i.ID > 3).map<InvoiceGroup>(partner => {
        let matches = this.all.invoices.filter(i => i.PartnerID == partner.ID);

        return {
          partner: partner,
          invoices: matches,
          ConfirmedCount: matches.count(i => i.State == InvoiceState.Confirmed),
          ConfirmedStamp: matches.max(i => i.State == InvoiceState.Confirmed ? i.Stamp : 0),

          EditableCount: matches.count(i => i.State == InvoiceState.Editable),
          EditableStamp: matches.max(i => i.State == InvoiceState.Editable ? i.Stamp : 0),

          LengthCount: matches.sum(i => i.LengthCount),
          WeightCount: matches.sum(i => i.WeightCount),
          LengthActualQuantity: matches.sum(i => i.LengthActualQuantity),
          WeightActualQuantity: matches.sum(i => i.WeightActualQuantity),
          Total: matches.sum(i => i.Total)
        }
      });

      this.invoices.source = [];
    })
  }

  ngOnInit(): void {

    main(() => {
      this.layout.media('footer', 'none');
      this.layout.title = Multilingual.getText("Nhập hàng khách trả");
    });
    
    this.synchronize();
  }

  public editStamp() {

    if (!this.editable) return;

    let ref = this.modal.show(CalendarComponent, { state: { selected: new Date(this.selected.invoice.Stamp) } });
    ref.content.change.subscribe(value => {
      this.selected.invoice.Stamp = value.getTime();
      ref.hide();
      this.edited = true;
    });
  }

  public get editable(): boolean {
    return this.selected.invoice && this.selected.invoice.State == InvoiceState.Editable;
  }

  public get creatable(): boolean {
    return !this.selected.invoice;
  }

  public get savable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.Details.length > 0 && this.selected.invoice.Details.count(i => i.ActualQuantity <= 0 || i.QuoteUnitPrice <= 0) < 1 && this.edited;
  }

  public get confirmable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.State == InvoiceState.Editable && this.selected.invoice.Details.length > 0 && this.selected.invoice.Details.count(i => i.QuoteQuantity <= 0 || i.QuoteUnitPrice <= 0) < 1 && !this.edited;
  }

  public get cancelable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.State == InvoiceState.Editable && ((this.selected.invoice.Details.length > 0 && !this.edited) || this.selected.invoice.ID == 0 || (this.selected.invoice.Details.length < 1 && this.edited));
  }

  public user(id: number): UserRecord {
    return this._users.get(id);
  }

  public partner(invoice: InvoiceRecord): PartnerRecord {
    return this._partners.get(invoice.PartnerID);
  }

  public statistics(field: string, groups: ReturningGroup[], unit?: ProductUnit): number {
    switch (field) {
      case "SKU": return groups.filter(i => !unit || unit == i.Unit).group(i => i.SKU).length;
      case "Color": return groups.filter(i => !unit || unit == i.Unit).group(i => i.SKU + "/" + i.Color).length;
      case "Count": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.length);
      case "ActualQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.sum(x => x.ActualQuantity));
      case "ReducedQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.sum(x => x.ActualQuantity - x.QuoteQuantity));
      case "QuoteQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.sum(x => x.QuoteQuantity));
      case "Total": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.sum(x => x.QuoteQuantity * x.QuoteUnitPrice));
      default: return 0;
    }

  }

  public state = invoiceState;

  public sum(field: string, unit?: ProductUnit): number {
    return this.statistics(field, this.selected.details, unit);
  }

  public extractor(field: string): SortableExtractor<any> {
    return {
      extract: i => {
        switch (field) {
          case 'Partner': return i.partner.Name;
          case 'Count': return i.LengthCount + i.WeightCount;
          default: return i[field];
        }
      }
    }
  }

  private refresh() {

    this.sync.notify(ReturnsComponent, false);

    this._products.synchronize();

    lu.returnable.source = this.returnable.source = [];
    let last = this.selected.partner;

    this.ds.goods("customer-resolvable", { CustomerID: this.selected.partner.ID }).subscribe(goods => {

      this.sync.when(ProductsService).commit(() => {

        if (goods && this.selected.partner == last) {

          goods = goods.group(i => i.SKU + "/" + i.Color).order(i => i.key).many(i => i.items.order(x => x.ExportUnitPrice * 10000000000 + x.ActualQuantity));

          lu.goods.source = goods;

          this.returnable.source = lu.returnable.source = goods

            .group(i => getKey({ ...i, QuoteQuantity: i.ExportQuoteQuantity, ImportInvoiceTag: i.ImportInvoiceTag })).map<ReturnableGroup>(i => ({

              SKU: i.items[0].SKU,
              Color: i.items[0].Color,
              Unit: i.items[0].Unit,
              ImportInvoiceTag: i.items[0].ImportInvoiceTag,
              ExportUnitPrice: i.items[0].ExportUnitPrice,
              ActualQuantity: i.items[0].ActualQuantity,
              QuoteQuantity: i.items[0].ExportQuoteQuantity,
              records: i.items
            }))

            .union(this._products.all.many(i => i.Colors.map<ReturnableGroup>(x => ({
              SKU: i.SKU,
              Color: x.Color,
              Unit: i.Unit,
              ActualQuantity: Infinity,
              ExportUnitPrice: 0,
              ImportInvoiceTag: 'n/a',
              QuoteQuantity: Infinity,
              records: []
            }))));
        }

        this.sync.notify(ReturnsComponent, true);

      });

    });
  }

  public select(group: InvoiceGroup) {

    this.selected.partner = group ? group.partner : null;
    this.invoices.source = group ? group.invoices : [];
  }

  public open(invoice: InvoiceRecord) {

    if (invoice.ID > 0 && invoice.Details.length == 0)

      this.sync.with(ReturnsComponent).repeat(() => this.ds.invoices('get', { ID: invoice.ID }).pipe(map(invoices => {
      if (invoices && invoices.length == 1) {
        invoice.Details = invoices[0].Details;

        return true;
      }
      return false;
    })));

    if (invoice.State == InvoiceState.Confirmed) {

      this.sync.when(PartnersService, ReturnsComponent).commit(() => {
        let modal = this.modal.show(ViewerComponent, { state: { invoice: invoice } });
        modal.content.unconfirmed.subscribe(result => invoice.State == result.State);
      });      
      return;
    }

    if (!this.selected.partner || this.selected.partner.ID != invoice.PartnerID) {
      this.select(this.partners.source.first(i => i.partner.ID == invoice.PartnerID));
    }

    this.refresh();

    this.sync.when(ReturnsComponent).commit(() => {
      this.selected.invoice = { ...invoice, Details: [] };
      this.selected.details = [];
      
      invoice.Details.forEach(detail => {

        let g = this.returnable.source.first(i => i.records.length > 0 && i.SKU == detail.SKU && i.Color == detail.Color && i.ImportInvoiceTag == detail.ImportInvoiceTag && i.ExportUnitPrice == detail.QuoteUnitPrice && i.ActualQuantity == detail.ActualQuantity && i.QuoteQuantity == detail.QuoteQuantity);

        if (g) {
          g.records = [g.records.first(i => i.ID == detail.ID)].union(g.records.filter(i => i.ID != detail.ID));
        }
        else {
          g = this.returnable.source.first(i => i.ActualQuantity == Infinity && i.SKU == detail.SKU && i.Color == detail.Color);
        }

        this.add(g, detail.ActualQuantity, detail.QuoteQuantity, detail.QuoteUnitPrice);
      });

      this.edited = false;
    });    
  }

  public close() {

    if (this.edited) {

      this.mb.confirms('discourage', "Đơn nhập hàng có các thay đổi chưa lưu lại. Đóng đơn sẽ không lưu lại các thay đổi đó. Bạn có muốn đóng đơn?",
        () => {
          this.edited = false;
          this.synchronize();
          return new Observable<boolean>(subscriber => subscriber.next(true));
        });
    }
    else {
      this.synchronize();
    }
  }

  public source(invoiceTag: string): string {

    let id = this._tags.get(invoiceTag);

    if (!id) return "";

    let pa = this._partners.get(id.PartnerID);

    return pa ? pa.Name : '';
  }

  public create() {

    this.open({
      ID: 0,
      Tag: null,
      CreatorID: this.api.user.ID,
      UpdatorID: this.api.user.ID,
      Icon: 'đ',
      Symbol: 'VND',
      State: InvoiceState.Editable,
      Type: InvoiceType.Import,
      Category: InvoiceCategory.Qualified,
      FirstStamp: new Date().getTime(),
      LastStamp: new Date().getTime(),
      Stamp: new Date().getTime(),
      Details: [],
      Allocations: [],
      PartnerID: this.selected.partner.ID,
      Total: 0
    });

    this.sync.when(ReturnsComponent).commit(() => this.editStamp());    
  }

  public add(product: ReturnableGroup, actualQuantity: number = NaN, quoteQuantity: number = NaN, unitPrice: number = NaN) {

    let good = product.records.shift();

    actualQuantity = !isNaN(actualQuantity) ? actualQuantity : good ? good.ActualQuantity : 0;
    quoteQuantity = !isNaN(quoteQuantity) ? quoteQuantity : good ? good.ExportQuoteQuantity - good.ExportDiscountQuantity : actualQuantity;
    unitPrice = !isNaN(unitPrice) ? unitPrice : good ? good.ExportUnitPrice : 0;

    let detail: ReturningGroup = this.selected.details.first(i => i.SKU == product.SKU && i.Color == product.Color && i.ImportInvoiceTag == product.ImportInvoiceTag && i.ExportUnitPrice == unitPrice && i.Unit == product.Unit, {

      Identified: good && good.ID > 0 ? true : false,
      SKU: product.SKU,
      Color: product.Color,
      Unit: product.Unit,
      ImportInvoiceTag: product.ImportInvoiceTag,
      ExportUnitPrice: unitPrice,
      records: []
    });

    let record: InvoiceDetailRecord = {
      ID: good ? good.ID : 0,
      SKU: product.SKU,
      Color: product.Color,
      Unit: product.Unit,
      ImportInvoiceTag: product.ImportInvoiceTag,
      ActualQuantity: actualQuantity,
      QuoteQuantity: quoteQuantity,
      QuoteUnitPrice: unitPrice,
      ImportOrderID: 0,
      ExportOrderID: 0,
      IsPriceEditable: good ? false : true,
    };

    this.selected.invoice.Details.push(record);
    detail.records.push(record);

    if (!this.selected.details.contains(detail)) this.selected.details.push(detail);

    if (product.records.length == 0 && good) {
      let index = this.returnable.pageIndex;
      this.returnable.source = this.returnable.source.filter(i => i != product);
      this.returnable.pageIndex = index;
    }

    this.edited = true;
  }

  public remove(records: InvoiceDetailRecord[]) {

    records.map(i => i).forEach(record => {

      let detail: ReturningGroup = this.selected.details.first(i => i.records.contains(record));

      detail.records.remove(record);
      if (detail.records.length < 1) this.selected.details.remove(detail);

      let good = lu.goods.get(record.ID);

      if (good) {

        let returnable = lu.returnable.get(getKey({ ...record, Unit: record.Unit, ImportInvoiceTag: record.ImportInvoiceTag, ExportUnitPrice: record.QuoteUnitPrice }));

        returnable.records.push(good);

        if (returnable.records.length == 1) {
          this.returnable.source = this.returnable.source.union([returnable]);
        }
      }

      this.selected.invoice.Details.remove(record);

      this.edited = true;
    });
  }

  public editLength(detail: InvoiceDetailRecord, input: NumberInputComponent) {

    let discount = detail.ActualQuantity - detail.QuoteQuantity;
    detail.ActualQuantity = input.value;
    detail.QuoteQuantity = detail.ActualQuantity - discount;
    this.edited = true;
  }

  public editDiscount(group: ReturningGroup, input: NumberInputComponent) {
    let discount = input.value / group.records.length;
    group.records.forEach(i => i.QuoteQuantity = i.ActualQuantity - discount);
    this.edited = true;
  }

  public editPrice(group: ReturningGroup, input: NumberInputComponent) {
    group.ExportUnitPrice = input.value;
    group.records.forEach(i => i.QuoteUnitPrice = group.ExportUnitPrice);
    this.edited = true;
  }

  public save() {

    this.sync.with(ReturnsComponent).commit(done => {

      this.ds.invoices(this.selected.invoice.ID > 0 ? 'update' : 'create', this.selected.invoice).subscribe(invoices => {

        if (invoices && invoices.length == 1) {

          this.edited = false;
          this.open(invoices[0]);
        }

        done();
      });
    });
  }

  public cancel() {

    if (this.selected.invoice.ID == 0 && this.selected.invoice.Details.length == 0) {
      this.edited = false;
      this.close();
      return;
    }

    this.mb.confirms("discourage", "Bạn muốn hủy đơn trả hàng đang mở?", () => {

      this.sync.notify(ReturnsComponent, false);

      return this.ds.invoices('cancel', this.selected.invoice).pipe(map(invoices => {

        this.sync.notify(ReturnsComponent, true);

        if (invoices) {

          this.invoices.source = this.invoices.source.filter(i => i.ID != this.selected.invoice.ID);
          this.edited = false;
          this.close();

          return true;
        }

        return false;
      }));
    });
  }

  public confirm() {

    this.mb.confirms('encourage', "Bạn muốn xác nhận đơn trả hàng đang mở?", () => {

      this.sync.notify(ReturnsComponent, false);

      return this.ds.invoices('confirm', this.selected.invoice).pipe(map(invoices => {

        this.sync.notify(ReturnsComponent, true);

        if (invoices && invoices.length == 1) {

          this.invoices.source = this.invoices.source.filter(i => i.ID != this.selected.invoice.ID).union(invoices);
          this.edited = false;
          this.close();

          return true;
        }

        return false;

      }));
    });
  }

}
